import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api/client';
import type { DashboardStats } from '../types/product';

const COLORS = ['#dc2626', '#f97316', '#eab308', '#22c55e', '#15803d'];

function StatCard({ label, value, color }: { label: string; value: string | number; color?: string }) {
  return (
    <div style={{ background: 'white', borderRadius: 8, padding: '20px 24px', border: '1px solid #e5e7eb', flex: '1 1 200px' }}>
      <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || '#111827' }}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDashboardStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading dashboard...</div>;
  }

  if (!stats) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>Failed to load dashboard</div>;
  }

  const scoreData = Object.entries(stats.score_distribution).map(([range, count]) => ({
    range,
    count,
  }));

  const sellingData = [
    { name: 'Selling', value: stats.selling_count },
    { name: 'Not Selling', value: stats.not_selling_count },
  ];

  const langData = Object.entries(stats.languages_coverage).map(([lang, pct]) => ({
    lang: lang.toUpperCase(),
    coverage: Math.round(pct * 100) / 100,
  }));

  return (
    <div style={{ padding: 24, overflow: 'auto', height: '100%', background: '#f9fafb' }}>
      <h2 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 600, color: '#111827' }}>Dashboard</h2>

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <StatCard label="Total Products" value={stats.total_products.toLocaleString()} />
        <StatCard label="Avg SEO Score" value={stats.avg_seo_score.toFixed(1)} color={stats.avg_seo_score >= 60 ? '#15803d' : '#dc2626'} />
        <StatCard label="Currently Selling" value={stats.selling_count.toLocaleString()} color="#059669" />
        <StatCard label="Not Selling" value={stats.not_selling_count.toLocaleString()} color="#6b7280" />
      </div>

      {/* Review stats */}
      {stats.review_stats && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard label="Approved" value={(stats.review_stats.approved || 0).toLocaleString()} color="#15803d" />
          <StatCard label="Pending Review" value={(stats.review_stats.pending || 0).toLocaleString()} color="#1e40af" />
          <StatCard label="Changes Requested" value={(stats.review_stats.changes_requested || 0).toLocaleString()} color="#92400e" />
          <StatCard label="Denied" value={(stats.review_stats.denied || 0).toLocaleString()} color="#991b1b" />
          <StatCard label="Unreviewed" value={(stats.review_stats.unreviewed || 0).toLocaleString()} color="#9ca3af" />
        </div>
      )}

      {/* Charts row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* Score distribution */}
        <div style={{ flex: '2 1 400px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#374151' }}>SEO Score Distribution</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={scoreData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="range" fontSize={12} />
              <YAxis fontSize={12} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                {scoreData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i] || '#3b82f6'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Selling pie */}
        <div style={{ flex: '1 1 250px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Selling Status</h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={sellingData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                <Cell fill="#22c55e" />
                <Cell fill="#e5e7eb" />
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom row */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* Language coverage */}
        <div style={{ flex: '1 1 300px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Language Coverage (%)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={langData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" domain={[0, 100]} fontSize={12} />
              <YAxis type="category" dataKey="lang" width={30} fontSize={12} />
              <Tooltip />
              <Bar dataKey="coverage" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Top categories */}
        <div style={{ flex: '1 1 250px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Top Categories</h3>
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {stats.top_categories.slice(0, 15).map((cat) => (
              <div key={cat.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#374151' }}>{cat.name}</span>
                <span style={{ color: '#6b7280', fontWeight: 500 }}>{cat.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top brands */}
        <div style={{ flex: '1 1 250px', background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Top Brands</h3>
          <div style={{ maxHeight: 200, overflow: 'auto' }}>
            {stats.top_brands.slice(0, 15).map((brand) => (
              <div key={brand.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ color: '#374151' }}>{brand.name}</span>
                <span style={{ color: '#6b7280', fontWeight: 500 }}>{brand.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent edits */}
      {stats.recent_edits.length > 0 && (
        <div style={{ marginTop: 16, background: 'white', borderRadius: 8, border: '1px solid #e5e7eb', padding: 20 }}>
          <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: '#374151' }}>Recent Edits</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Product</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>User</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>Fields</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280', fontWeight: 500 }}>When</th>
              </tr>
            </thead>
            <tbody>
              {stats.recent_edits.map((edit, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 8px' }}>{edit.display_name || edit.cprcode}</td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{edit.user}</td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{edit.fields.join(', ')}</td>
                  <td style={{ padding: '6px 8px', color: '#6b7280' }}>{new Date(edit.timestamp).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
